import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Minus, 
  Coins, 
  TrendingUp, 
  TrendingDown,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  History,
  Settings,
  Lock,
  Unlock,
  Info
} from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { ProofOfReserveBadge } from '../../components/tokenization';
import { PageHeader, InfoTooltip, ContextualHelp } from '../../components/help';
import { tokenizationHelp } from '../../config/helpContent';
import { api } from '../../lib/api';
import { CustodyStatusIndicator, CoverageRatioDisplay } from '../../components/financial-security';
import { useDeployPrerequisites, useMintPrerequisites } from '../../hooks/useFinancialSecurity';

interface SupplyInfo {
  assetId: string;
  currentSupply: number;
  maxSupply: number | null;
  circulatingSupply: number;
  lockedSupply: number;
  burnedSupply: number;
  supplyPolicy: {
    allowAdditionalMint: boolean;
    maxSupply: number | null;
    mintRequiresApproval: boolean;
    mintApprovalThreshold: number | null;
    allowBurn: boolean;
    burnRequiresApproval: boolean;
    burnApprovalThreshold: number | null;
    whoCanBurn: 'ISSUER_ONLY' | 'HOLDER' | 'ANYONE';
  };
  proofOfReserve: {
    reserveAmount: number;
    coverageRatio: number;
    lastUpdated: string;
    isVerified: boolean;
  } | null;
}

interface SupplyOperation {
  id: string;
  type: 'MINT' | 'BURN';
  amount: number;
  reason: string;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'FAILED';
  requestedBy: string;
  requestedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  txHash?: string;
  rejectionReason?: string;
}

interface Asset {
  id: string;
  name: string;
  symbol: string;
  unitPrice: number;
  status: string;
  contractAddress?: string;
}

export function SupplyManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'mint' | 'burn'>('mint');
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Financial Security Prerequisites
  const { data: deployPrerequisites, isLoading: deployPrereqLoading } = useDeployPrerequisites(id);
  const { data: mintPrerequisites, isLoading: mintPrereqLoading } = useMintPrerequisites(
    id, 
    amount > 0 ? String(amount) : undefined
  );

  const { data: asset, isLoading: assetLoading } = useQuery<Asset>({
    queryKey: ['asset', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: supplyInfo, isLoading: supplyLoading } = useQuery<SupplyInfo>({
    queryKey: ['supply-info', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}/supply`);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: operations } = useQuery<SupplyOperation[]>({
    queryKey: ['supply-operations', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}/supply/history`);
      return res.data;
    },
    enabled: !!id,
  });

  const mintMutation = useMutation({
    mutationFn: async (data: { amount: number; reason: string }) => {
      return api.post(`/tokenization/assets/${id}/supply/mint`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-info', id] });
      queryClient.invalidateQueries({ queryKey: ['supply-operations', id] });
      setAmount(0);
      setReason('');
      setShowConfirmation(false);
    }
  });

  const burnMutation = useMutation({
    mutationFn: async (data: { amount: number; reason: string }) => {
      return api.post(`/tokenization/assets/${id}/supply/burn`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-info', id] });
      queryClient.invalidateQueries({ queryKey: ['supply-operations', id] });
      setAmount(0);
      setReason('');
      setShowConfirmation(false);
    }
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR').format(value);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const handleSubmit = () => {
    if (amount <= 0 || !reason.trim()) return;
    
    if (activeTab === 'mint') {
      mintMutation.mutate({ amount, reason });
    } else {
      burnMutation.mutate({ amount, reason });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Concluído</Badge>;
      case 'PENDING_APPROVAL':
        return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" /> Aguardando Aprovação</Badge>;
      case 'APPROVED':
        return <Badge variant="info" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Aprovado</Badge>;
      case 'REJECTED':
        return <Badge variant="error" className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejeitado</Badge>;
      case 'FAILED':
        return <Badge variant="error" className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Falhou</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const canMint = supplyInfo?.supplyPolicy.allowAdditionalMint && 
    (!supplyInfo?.maxSupply || supplyInfo.currentSupply < supplyInfo.maxSupply);
  
  const canBurn = supplyInfo?.supplyPolicy.allowBurn;

  // Financial security validations
  const custodyStatus = deployPrerequisites?.custodyDetails?.status || null;
  const canDeployByPolicy = deployPrerequisites?.canDeploy ?? true;
  const canMintByPolicy = mintPrerequisites?.canMint ?? true;
  const currentCoverageRatio = mintPrerequisites?.currentCoverageRatio ?? supplyInfo?.proofOfReserve?.coverageRatio ?? 100;
  const projectedCoverageRatio = mintPrerequisites?.projectedCoverageRatio;
  const coverageThreshold = mintPrerequisites?.policy?.minCoverageRatioThreshold ?? 100;

  const requiresApproval = activeTab === 'mint' 
    ? supplyInfo?.supplyPolicy.mintRequiresApproval
    : supplyInfo?.supplyPolicy.burnRequiresApproval;

  const approvalThreshold = activeTab === 'mint'
    ? supplyInfo?.supplyPolicy.mintApprovalThreshold
    : supplyInfo?.supplyPolicy.burnApprovalThreshold;

  const willRequireApproval = requiresApproval && 
    (!approvalThreshold || amount * (asset?.unitPrice || 0) >= approvalThreshold);

  // Query para listar ativos quando não há ID selecionado
  const { data: assets, isLoading: assetsLoading } = useQuery<Asset[]>({
    queryKey: ['tokenization-assets-for-supply'],
    queryFn: async () => {
      const res = await api.get('/tokenization/assets', {
        params: { status: 'ACTIVE,MINTED,APPROVED' }
      });
      return res.data.data || res.data;
    },
    enabled: !id,
  });

  if (!id) {
    // Mostrar lista de ativos para selecionar
    if (assetsLoading) {
      return (
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface-800 rounded w-64" />
            <div className="h-96 bg-surface-800 rounded-xl" />
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 space-y-6">
        <PageHeader
          title="Gestão de Supply"
          description="Selecione um ativo para gerenciar a emissão (mint) e queima (burn) de tokens"
          icon={<Coins className="w-6 h-6 text-primary-400" />}
          module="Tokenização"
        />

        {!assets || assets.length === 0 ? (
          <Card className="p-8 text-center">
            <Coins className="w-12 h-12 text-surface-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-surface-200 mb-2">Nenhum ativo disponível</h3>
            <p className="text-surface-400 mb-4">
              Você precisa ter ativos aprovados ou tokenizados para gerenciar o supply.
            </p>
            <Button onClick={() => navigate('/admin/tokenization/assets')}>
              Ver Ativos
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {assets.map((a) => (
              <Card 
                key={a.id} 
                className="p-4 hover:bg-surface-800/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/tokenization/supply/${a.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                      <Coins className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-surface-100">{a.name}</h3>
                      <p className="text-sm text-surface-400">{a.symbol}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-surface-200">{formatCurrency(a.unitPrice)}/token</p>
                    <Badge variant={a.status === 'ACTIVE' ? 'success' : a.status === 'MINTED' ? 'info' : 'warning'}>
                      {a.status}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (assetLoading || supplyLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!asset || !supplyInfo) {
    return (
      <div className="p-6 text-center">
        <p className="text-surface-400">Ativo não encontrado</p>
        <Button className="mt-4" onClick={() => navigate('/admin/tokenization/supply')}>
          Voltar para lista
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Help */}
      <div className="flex items-center gap-4 mb-2">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-surface-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-surface-400" />
        </button>
      </div>
      
      <PageHeader
        title={`Gestão de Supply: ${asset.name} (${asset.symbol})`}
        description="Controle a emissão (mint) e queima (burn) de tokens. Operações refletem mudanças no lastro do ativo."
        icon={<Settings className="w-6 h-6 text-primary-400" />}
        module="Tokenização"
        helpContent={tokenizationHelp.supplyManagement.helpContent}
      />

      {/* Supply Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Coins className="w-5 h-5 text-primary-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="text-sm text-surface-400">Supply Atual</p>
                <InfoTooltip content="Total de tokens emitidos até o momento, incluindo tokens em circulação e bloqueados." />
              </div>
              <p className="text-xl font-bold text-surface-100">
                {formatNumber(supplyInfo.currentSupply)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="text-sm text-surface-400">Em Circulação</p>
                <InfoTooltip content="Tokens disponíveis para negociação no mercado. Exclui tokens bloqueados ou em custódia." />
              </div>
              <p className="text-xl font-bold text-surface-100">
                {formatNumber(supplyInfo.circulatingSupply)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="text-sm text-surface-400">Supply Máximo</p>
                <InfoTooltip content="Limite máximo de tokens que podem ser emitidos. 'Ilimitado' significa que não há teto definido." />
              </div>
              <p className="text-xl font-bold text-surface-100">
                {supplyInfo.maxSupply ? formatNumber(supplyInfo.maxSupply) : 'Ilimitado'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="text-sm text-surface-400">Queimados</p>
                <InfoTooltip content="Tokens destruídos permanentemente através de operações de burn. Reduzem o supply total." />
              </div>
              <p className="text-xl font-bold text-surface-100">
                {formatNumber(supplyInfo.burnedSupply)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mint/Burn Form */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('mint')}
                disabled={!canMint}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'mint'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
                } ${!canMint ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Plus className="w-5 h-5" />
                Mint (Emitir)
              </button>
              <button
                onClick={() => setActiveTab('burn')}
                disabled={!canBurn}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'burn'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
                } ${!canBurn ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Minus className="w-5 h-5" />
                Burn (Queimar)
              </button>
            </div>

            {/* Policy Info */}
            {!canMint && activeTab === 'mint' && (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-yellow-400">
                    {supplyInfo.maxSupply && supplyInfo.currentSupply >= supplyInfo.maxSupply
                      ? 'Supply máximo atingido. Não é possível emitir mais tokens.'
                      : 'Mint adicional não está habilitado para este ativo.'}
                  </p>
                </div>
              </div>
            )}

            {!canBurn && activeTab === 'burn' && (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-yellow-400">Burn não está habilitado para este ativo.</p>
                </div>
              </div>
            )}

            {/* Form */}
            {((activeTab === 'mint' && canMint) || (activeTab === 'burn' && canBurn)) && (
              <div className="space-y-4">
                {/* Mint Prerequisites Warning */}
                {activeTab === 'mint' && !canMintByPolicy && amount > 0 && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Lock className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Mint Bloqueado</p>
                        <p className="text-sm text-red-300 mt-1">
                          {mintPrerequisites?.wouldUndercollateralize 
                            ? `Esta operação reduziria a taxa de cobertura para ${projectedCoverageRatio?.toFixed(2)}%, abaixo do mínimo de ${coverageThreshold}%.`
                            : 'Pré-requisitos de segurança não atendidos.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Coverage Ratio Preview for Mint */}
                {activeTab === 'mint' && amount > 0 && mintPrerequisites && (
                  <div className="p-4 bg-surface-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Info className="w-4 h-4 text-primary-400" />
                      <p className="text-sm font-medium text-surface-200">Impacto na Cobertura</p>
                    </div>
                    <CoverageRatioDisplay
                      ratio={currentCoverageRatio}
                      threshold={coverageThreshold}
                      projectedRatio={projectedCoverageRatio}
                      size="md"
                    />
                    <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="text-surface-500">Supply Atual</p>
                        <p className="text-surface-200 font-mono">{mintPrerequisites.currentSupply}</p>
                      </div>
                      <div>
                        <p className="text-surface-500">Supply Projetado</p>
                        <p className="text-surface-200 font-mono">{mintPrerequisites.projectedSupply}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-surface-400 mb-1">
                    Quantidade de Tokens
                  </label>
                  <input
                    type="number"
                    value={amount || ''}
                    onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 text-surface-100 text-lg"
                    placeholder="0"
                    min={1}
                    max={activeTab === 'burn' ? supplyInfo.circulatingSupply : undefined}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-surface-500">
                      Valor: {formatCurrency(amount * asset.unitPrice)}
                    </span>
                    {activeTab === 'mint' && supplyInfo.maxSupply && (
                      <span className="text-xs text-surface-500">
                        Disponível: {formatNumber(supplyInfo.maxSupply - supplyInfo.currentSupply)}
                      </span>
                    )}
                    {activeTab === 'burn' && (
                      <span className="text-xs text-surface-500">
                        Disponível: {formatNumber(supplyInfo.circulatingSupply)}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-surface-400 mb-1">
                    Motivo / Justificativa
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 text-surface-100"
                    rows={3}
                    placeholder={activeTab === 'mint' 
                      ? 'Ex: Aumento de lastro, nova captação...'
                      : 'Ex: Resgate de tokens, redução de supply...'}
                  />
                </div>

                {willRequireApproval && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      <p className="text-yellow-400">
                        Esta operação requer aprovação antes de ser executada.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant={activeTab === 'mint' ? 'primary' : 'secondary'}
                    className={`flex-1 ${activeTab === 'burn' ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : ''}`}
                    onClick={() => setShowConfirmation(true)}
                    disabled={
                      amount <= 0 || 
                      !reason.trim() || 
                      (activeTab === 'mint' && !canMintByPolicy && amount > 0)
                    }
                  >
                    {activeTab === 'mint' ? (
                      <>
                        {canMintByPolicy || amount === 0 ? (
                          <Plus className="w-4 h-4 mr-2" />
                        ) : (
                          <Lock className="w-4 h-4 mr-2" />
                        )}
                        Emitir {formatNumber(amount)} Tokens
                      </>
                    ) : (
                      <>
                        <Minus className="w-4 h-4 mr-2" />
                        Queimar {formatNumber(amount)} Tokens
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Financial Security Status */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-400" />
              Segurança Financeira
            </h3>
            
            {/* Custody Status */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <p className="text-sm text-surface-400">Status da Custódia</p>
                  <InfoTooltip content="A custódia deve estar ATIVA para operações de deploy e mint. Isso garante que os tokens são lastreados por ativos reais." />
                </div>
                <CustodyStatusIndicator status={custodyStatus} size="md" />
                {deployPrerequisites?.custodyDetails && (
                  <div className="mt-2 text-xs text-surface-500">
                    <p>Custodiante: {deployPrerequisites.custodyDetails.custodianName}</p>
                    <p>Ref: {deployPrerequisites.custodyDetails.custodyReference}</p>
                  </div>
                )}
              </div>

              {/* Coverage Ratio */}
              {supplyInfo?.proofOfReserve && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <p className="text-sm text-surface-400">Taxa de Cobertura</p>
                    <InfoTooltip content="Relação entre o valor em custódia e o valor total dos tokens emitidos. Deve ser >= 100% para garantir lastro completo." />
                  </div>
                  <CoverageRatioDisplay
                    ratio={currentCoverageRatio}
                    threshold={coverageThreshold}
                    projectedRatio={amount > 0 ? projectedCoverageRatio : undefined}
                    size="sm"
                  />
                </div>
              )}

              {/* Blocking Issues */}
              {deployPrerequisites?.blockingIssues && deployPrerequisites.blockingIssues.length > 0 && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs font-medium text-red-400 mb-1">Problemas Bloqueantes:</p>
                  <ul className="text-xs text-red-300 space-y-1">
                    {deployPrerequisites.blockingIssues.map((issue, idx) => (
                      <li key={idx} className="flex items-start gap-1">
                        <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {mintPrerequisites?.blockingIssues && mintPrerequisites.blockingIssues.length > 0 && amount > 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-xs font-medium text-yellow-400 mb-1">Avisos para Mint:</p>
                  <ul className="text-xs text-yellow-300 space-y-1">
                    {mintPrerequisites.blockingIssues.map((issue, idx) => (
                      <li key={idx} className="flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>

          {/* Proof of Reserve */}
          {supplyInfo.proofOfReserve && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-4">Proof of Reserve</h3>
              <ProofOfReserveBadge
                coverageRatio={supplyInfo.proofOfReserve.coverageRatio}
                lastVerified={supplyInfo.proofOfReserve.lastUpdated}
                showDetails
              />
              <div className="mt-4 pt-4 border-t border-surface-700 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-400">Reserva</span>
                  <span className="text-surface-100">{formatCurrency(supplyInfo.proofOfReserve.reserveAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Supply Valor</span>
                  <span className="text-surface-100">{formatCurrency(supplyInfo.currentSupply * asset.unitPrice)}</span>
                </div>
              </div>
            </Card>
          )}

          {/* Supply Policy */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Política de Supply</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Mint Adicional</span>
                <Badge variant={supplyInfo.supplyPolicy.allowAdditionalMint ? 'success' : 'error'}>
                  {supplyInfo.supplyPolicy.allowAdditionalMint ? 'Permitido' : 'Bloqueado'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Burn</span>
                <Badge variant={supplyInfo.supplyPolicy.allowBurn ? 'success' : 'error'}>
                  {supplyInfo.supplyPolicy.allowBurn ? 'Permitido' : 'Bloqueado'}
                </Badge>
              </div>
              {supplyInfo.supplyPolicy.mintRequiresApproval && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Aprovação Mint</span>
                  <span className="text-surface-100">Obrigatória</span>
                </div>
              )}
              {supplyInfo.supplyPolicy.burnRequiresApproval && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Aprovação Burn</span>
                  <span className="text-surface-100">Obrigatória</span>
                </div>
              )}
            </div>
          </Card>

          {/* Contract Info */}
          {asset.contractAddress && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-4">Contrato</h3>
              <p className="text-xs text-surface-400 break-all font-mono bg-surface-800 p-2 rounded">
                {asset.contractAddress}
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Operations History */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-surface-100">Histórico de Operações</h3>
        </div>

        {operations && operations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-800">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Quantidade</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Motivo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-800">
                {operations.map((op) => (
                  <tr key={op.id} className="hover:bg-surface-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {op.type === 'MINT' ? (
                          <Plus className="w-4 h-4 text-green-400" />
                        ) : (
                          <Minus className="w-4 h-4 text-red-400" />
                        )}
                        <span className={op.type === 'MINT' ? 'text-green-400' : 'text-red-400'}>
                          {op.type === 'MINT' ? 'Mint' : 'Burn'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {formatNumber(op.amount)}
                    </td>
                    <td className="px-4 py-3 text-surface-300 max-w-xs truncate">
                      {op.reason}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(op.status)}
                    </td>
                    <td className="px-4 py-3 text-surface-400 text-sm">
                      {formatDate(op.requestedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400">Nenhuma operação de supply realizada</p>
          </div>
        )}
      </Card>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Confirmar {activeTab === 'mint' ? 'Emissão' : 'Queima'}
            </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-surface-400">Operação</span>
                <span className={activeTab === 'mint' ? 'text-green-400' : 'text-red-400'}>
                  {activeTab === 'mint' ? 'Mint' : 'Burn'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Quantidade</span>
                <span className="text-surface-100 font-medium">{formatNumber(amount)} tokens</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Valor</span>
                <span className="text-surface-100">{formatCurrency(amount * asset.unitPrice)}</span>
              </div>
              {willRequireApproval && (
                <div className="pt-3 border-t border-surface-700">
                  <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Requer aprovação
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowConfirmation(false)}
              >
                Cancelar
              </Button>
              <Button
                variant={activeTab === 'mint' ? 'primary' : 'danger'}
                className="flex-1"
                onClick={handleSubmit}
                disabled={mintMutation.isPending || burnMutation.isPending}
              >
                {mintMutation.isPending || burnMutation.isPending ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>

            {(mintMutation.isError || burnMutation.isError) && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">Erro ao processar operação. Tente novamente.</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Contextual Help FAQs */}
      <ContextualHelp faqs={tokenizationHelp.faqs} />
    </div>
  );
}

export default SupplyManagement;
